import Typography from '@mui/material/Typography';
import type { ElementType, ReactNode } from 'react';

const TEXT_COLOR = {
  primary: 'text.primary',
  secondary: 'text.secondary',
  disabled: 'text.disabled',
  error: 'error.main',
  // The brand tint, for the ONE value in a group that the eye must land on first (the lock-code
  // carril's «siguiente»). Not a semantic state like `error` — it carries emphasis, not meaning,
  // so it never stands alone: whatever it marks is labelled in text as well.
  brand: 'primary.main',
  // A de-emphasized tone that still clears AA: `text.secondary` (#ACA5BB) sits at 2.37:1,
  // below even the 3:1 large-text floor, so muted maps to `grey.600` (#757575, ≈4.6:1 on
  // white) instead — for content that must read as de-emphasized without failing contrast.
  muted: 'grey.600',
} as const;

export interface TextProps {
  variant?:
    | 'h1'
    | 'h2'
    | 'h3'
    | 'h4'
    | 'h5'
    | 'h6'
    | 'subtitle1'
    | 'subtitle2'
    | 'body1'
    | 'body2'
    | 'button'
    | 'caption'
    | 'overline';
  color?: 'primary' | 'secondary' | 'disabled' | 'error' | 'brand' | 'muted';
  component?: ElementType;
  align?: 'inherit' | 'left' | 'center' | 'right';
  // Bold weight, for section labels/titles that need emphasis. A curated escape hatch
  // so views don't reach past the facade with `.MuiTypography-*` sx overrides.
  bold?: boolean;
  // Compact line-height (1), for section labels that should sit close to the value
  // below them — the `overline` variant's default 2.66 leaves a wide gap otherwise.
  dense?: boolean;
  gutterBottom?: boolean;
  // Single-line truncation with an ellipsis (maps to Typography's noWrap); the caller
  // constrains the width (e.g. a max-width container).
  noWrap?: boolean;
  // Hover title — a standard DOM attribute, exposed for the same reason Box exposes `role`: the
  // companion to `noWrap`, since CSS truncation takes the tail off the screen and leaves the
  // accessible name whole, so this restores for the eye what only the eye lost.
  title?: string;
  // A standard DOM attribute too (like Box's `id`), for the heading that NAMES a dialog: ARIA
  // references by id and nothing else, so a `labelledBy` has to have an id to point AT — and this
  // façade takes no `sx` and no rest props, leaving a view with no other route to one than dropping
  // to a raw MUI node for its own title.
  id?: string;
  children: ReactNode;
}

export function Text({
  variant = 'body1',
  color = 'primary',
  component,
  align,
  bold,
  dense,
  gutterBottom,
  noWrap,
  title,
  id,
  children,
}: TextProps) {
  // Merge the curated style escape hatches into a single sx so they compose.
  const sx = {
    ...(bold ? { fontWeight: 700 } : {}),
    ...(dense ? { lineHeight: 1 } : {}),
  };

  // Omit unset optionals rather than forwarding `undefined`: the project runs
  // with exactOptionalPropertyTypes, which rejects `prop={undefined}`.
  const base = {
    variant,
    color: TEXT_COLOR[color],
    ...(align !== undefined ? { align } : {}),
    ...(Object.keys(sx).length > 0 ? { sx } : {}),
    ...(gutterBottom !== undefined ? { gutterBottom } : {}),
    ...(noWrap !== undefined ? { noWrap } : {}),
    ...(title !== undefined ? { title } : {}),
    ...(id !== undefined ? { id } : {}),
  };

  // `component` is Typography's polymorphic prop: it only exists on the
  // overload where a concrete element is supplied, so forward it only when set.
  return component ? (
    <Typography {...base} component={component}>
      {children}
    </Typography>
  ) : (
    <Typography {...base}>{children}</Typography>
  );
}
